import * as React from 'react';
import { Box, Grid, Link, Typography } from '@mui/material';

import { useCurrencyString } from 'lib/hooks/useCurrencyString';
import { ResourceLink } from 'lib/ResourceLink';
import { isString } from 'lib/typeguards';
import { LoadingTypography } from 'lib/LoadingTypoghraphy';
import { CUSTOMERS_VIEW, LOCATION_VIEW } from 'config/routes';
import { customerApi } from 'components/customers/customerApi';
import { locationApi } from 'components/location/LocationApi';
import { getTimeslotTimes } from 'components/timeslots/helpers';
import { IOrder, OrderScenario, OrderScenarioLabels } from '../model/Order';

type OrderOverviewProps = {
    order: IOrder;
    loading?: boolean;
};

type CustomerLink = {
    id: string;
    name: string;
    link: string;
};

export const OrderOverview = ({ order, loading }: OrderOverviewProps) => {
    const [customerPhoneNumber, setCustomerPhoneNumber] = React.useState<string>();
    const getCurrencyString = useCurrencyString(order?.currencyCode);

    const collectionTime = React.useMemo(() => {
        if (order?.scenario === OrderScenario.PREORDER) {
            if (order?.timeSlot) {
                return getTimeslotTimes(order.timeSlot, true);
            }
            return 'ASAP';
        }
        return undefined;
    }, [order?.scenario, order?.timeSlot]);

    const customerLinks = React.useMemo<Array<CustomerLink>>(() => {
        const customers = order?.users;
        if (!customers || customers.length === 0) {
            return [];
        }
        return customers.map(customer => {
            const parsedLink = CUSTOMERS_VIEW.replace(':customerId', customer.userId);
            const name = customer.firstName + ' ' + customer.lastName;
            return {
                id: customer.userId,
                name,
                link: parsedLink
            };
        });
    }, [order?.users]);

    const renderCustomerLink = React.useCallback(
        (customer: CustomerLink, index: number) => (
            <React.Fragment key={customer.id}>
                <Link variant="body1" href={customer.link} target="_blank">
                    {customer.name}
                </Link>
                {index < customerLinks.length - 1 && <span>, </span>}
            </React.Fragment>
        ),
        [customerLinks]
    );

    React.useEffect(() => {
        if (order?.scenario === OrderScenario.PREORDER && order?.users[0]) {
            customerApi
                .getUser(order?.users[0].userId)
                .then(result => {
                    if (!result.ok) {
                        throw new Error(result.body.message);
                    }
                    setCustomerPhoneNumber(result.body.phoneNumberLastUsedToOrder);
                })
                .catch(() => {
                    setCustomerPhoneNumber(undefined);
                });
        }
    }, [order?.scenario, order?.users]);

    return (
        <Box paddingY={2}>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}
                >
                    <Typography variant="body2">Location</Typography>
                    <LoadingTypography variant="body1" loading={loading} width={200}>
                        <ResourceLink
                            id={order?.locationId}
                            consoleLink={LOCATION_VIEW}
                            pathParam=":locationId"
                            crud={locationApi}
                            title="title"
                        />
                    </LoadingTypography>
                </Grid>
                <Grid
                    size={{
                        xs: 6,
                        md: 3,
                        lg: 2
                    }}
                >
                    <Typography variant="body2">Scenario</Typography>
                    <LoadingTypography variant="body1" loading={loading} sx={{ width: '100%' }}>
                        {OrderScenarioLabels[order?.scenario]}
                    </LoadingTypography>
                </Grid>
                {customerLinks.length > 0 && (
                    <Grid
                        size={{
                            xs: 6,
                            md: 3,
                            lg: 2
                        }}
                    >
                        <Typography variant="body2">Customer</Typography>
                        {customerLinks.map(renderCustomerLink)}
                    </Grid>
                )}
                {order?.delivery?.dropoffAddress && (
                    <Grid
                        size={{
                            xs: 6,
                            md: 3,
                            lg: 2
                        }}
                    >
                        <Typography variant="body2">Delivery Address</Typography>
                        <LoadingTypography variant="body1" loading={loading} sx={{ width: '100%' }}>
                            {order?.delivery?.dropoffAddress?.address?.line1},{' '}
                            {order?.delivery?.dropoffAddress?.address?.city},{' '}
                            {order?.delivery?.dropoffAddress?.address?.postalCode}
                        </LoadingTypography>
                    </Grid>
                )}
                <Grid
                    size={{
                        xs: 6,
                        md: 3,
                        lg: 2
                    }}
                >
                    <Typography variant="body2">Order Number</Typography>
                    <LoadingTypography variant="body1" loading={loading} sx={{ width: '100%' }}>
                        {order?.id}
                    </LoadingTypography>
                </Grid>
                <Grid
                    size={{
                        xs: 6,
                        md: 3,
                        lg: 2
                    }}
                >
                    <Typography variant="body2">Order Value</Typography>
                    <LoadingTypography variant="body1" loading={loading} sx={{ width: '100%' }}>
                        {getCurrencyString(order?.total?.total)}
                    </LoadingTypography>
                </Grid>
                {order?.total?.tips > 0 && (
                    <Grid
                        size={{
                            xs: 6,
                            md: 3,
                            lg: 2
                        }}
                    >
                        <Typography variant="body2">Tips</Typography>
                        <LoadingTypography variant="body1" loading={loading} sx={{ width: '100%' }}>
                            {getCurrencyString(order?.total?.tips)}
                        </LoadingTypography>
                    </Grid>
                )}
                {isString(order?.deliveryLocation) && (
                    <Grid
                        size={{
                            xs: 6,
                            md: 3,
                            lg: 2
                        }}
                    >
                        <Typography variant="body2">Table</Typography>
                        <Typography variant="body1">{order?.deliveryLocation}</Typography>
                    </Grid>
                )}
                {isString(collectionTime) && (
                    <Grid
                        size={{
                            xs: 6,
                            md: 3,
                            lg: 2
                        }}
                    >
                        <Typography variant="body2">Collection Time</Typography>
                        <Typography variant="body1">{collectionTime}</Typography>
                    </Grid>
                )}
                {isString(customerPhoneNumber) && (
                    <Grid
                        size={{
                            xs: 6,
                            md: 3,
                            lg: 2
                        }}
                    >
                        <Typography variant="body2">Contact Details</Typography>
                        <Typography variant="body1">{customerPhoneNumber}</Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};
